if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.querySelector('[data-product-video-sound]')?.addEventListener('click', (event) => {
          event.stopPropagation();
          const activeItem = this.querySelector('.product__media-item.is-active');
          const video = activeItem?.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          this.resumeProductVideo(activeItem, video);
        });
        this.querySelector('[data-product-video-play]')?.addEventListener('click', (event) => {
          event.stopPropagation();
          const activeItem = this.querySelector('.product__media-item.is-active');
          const video = activeItem?.querySelector('video');
          if (video) this.resumeProductVideo(activeItem, video);
        });
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.enableThumbnailPointerDrag();
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      enableThumbnailPointerDrag() {
        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        if (typeof this.elements.thumbnails.setSlidePosition === 'function') {
          this.elements.thumbnails.setSlidePosition = (position) => this.smoothThumbnailScroll(position);
        }

        let activePointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let suppressClick = false;

        const finishDrag = (event) => {
          if (activePointerId === null || event.pointerId !== activePointerId) return;
          const pointerId = activePointerId;
          activePointerId = null;
          slider.classList.remove('is-pointer-dragging');
          if (slider.hasPointerCapture?.(pointerId)) slider.releasePointerCapture(pointerId);
          window.setTimeout(() => { suppressClick = false; });
        };

        slider.addEventListener('pointerdown', (event) => {
          const compactWidth = Math.min(window.innerWidth, document.documentElement.clientWidth, slider.clientWidth);
          if (compactWidth > 749) return;
          if (event.pointerType === 'touch' || event.button !== 0) return;
          if (slider.scrollWidth <= slider.clientWidth) return;
          activePointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = slider.scrollLeft;
          suppressClick = false;
        });

        slider.addEventListener('pointermove', (event) => {
          if (activePointerId === null || event.pointerId !== activePointerId) return;
          const distance = event.clientX - startX;
          if (!suppressClick && Math.abs(distance) < 4) return;
          if (!suppressClick) {
            suppressClick = true;
            slider.setPointerCapture?.(activePointerId);
            slider.classList.add('is-pointer-dragging');
          }
          event.preventDefault();
          slider.scrollLeft = startScrollLeft - distance;
        });

        slider.addEventListener('pointerup', finishDrag);
        slider.addEventListener('pointercancel', finishDrag);
        slider.addEventListener('lostpointercapture', finishDrag);
        slider.addEventListener('click', (event) => {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!options.fromProductSlider) {
          this.syncSliderToMedia(mediaId);
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!options.fromProductSlider && !this.productSlider && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setProductSlider(swiper) {
        this.productSlider = swiper;
      }

      syncSliderToMedia(mediaId) {
        if (!this.productSlider || this.productSlider.destroyed) return;

        window.requestAnimationFrame(() => {
          if (!this.productSlider || this.productSlider.destroyed) return;
          this.productSlider.update();
          const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
          const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (index < 0 || this.productSlider.activeIndex === index) return;
          this.productSlider.slideTo(index);
        });
      }

      smoothThumbnailScroll(target) {
        const slider = this.elements.thumbnails?.slider;
        if (!slider) return;

        this.thumbnailScrollCleanup?.();
        const destination = Math.max(0, Math.min(target, slider.scrollWidth - slider.clientWidth));
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          slider.scrollLeft = destination;
          return;
        }

        slider.classList.add('is-programmatic-scrolling');
        const finish = () => {
          slider.removeEventListener('scrollend', finish);
          window.clearTimeout(this.thumbnailScrollTimer);
          slider.classList.remove('is-programmatic-scrolling');
          if (this.thumbnailScrollCleanup === finish) this.thumbnailScrollCleanup = null;
        };

        this.thumbnailScrollCleanup = finish;
        slider.addEventListener('scrollend', finish, { once: true });
        slider.scrollTo({ left: destination, behavior: 'smooth' });
        this.thumbnailScrollTimer = window.setTimeout(finish, 800);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.smoothThumbnailScroll(thumbnail.offsetLeft);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        const changedMedia = this.lastPlayedMedia !== activeItem;
        const repeatedSync = this.hasAttribute('data-product-slider') &&
          this.lastPlayedMedia === activeItem && deferredMedia?.hasAttribute('loaded');
        if (!repeatedSync) {
          window.pauseAllMedia();
          if (deferredMedia) deferredMedia.loadContent(false);
        }
        this.lastPlayedMedia = activeItem;

        const video = deferredMedia?.querySelector('video');
        if (this.hasAttribute('data-product-slider')) {
          this.toggleAttribute('data-product-video-active', Boolean(video));
          this.querySelector('[data-product-video-sound]')?.toggleAttribute('hidden', !video);
          this.querySelector('[data-product-video-play]')?.setAttribute('hidden', '');
        }
        if (!video || !this.hasAttribute('data-product-slider')) return;

        if (changedMedia) {
          video.muted = true;
          video.currentTime = 0;
        }
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        if (!video.dataset.productVideoBound) {
          video.dataset.productVideoBound = 'true';
          video.addEventListener('volumechange', () => this.updateProductVideoControls(activeItem, video));
          video.addEventListener('playing', () => {
            if (!activeItem.classList.contains('is-active')) video.pause();
            else this.querySelector('[data-product-video-play]')?.setAttribute('hidden', '');
          });
          video.addEventListener('ended', () => {
            if (activeItem.classList.contains('is-active')) {
              this.querySelector('[data-product-video-play]')?.removeAttribute('hidden');
            }
          });
        }
        this.resumeProductVideo(activeItem, video);
      }

      updateProductVideoControls(activeItem, video) {
        if (!activeItem.classList.contains('is-active')) return;
        const button = this.querySelector('[data-product-video-sound]');
        if (!button) return;
        button.dataset.muted = String(video.muted);
        const label = video.muted ? button.dataset.unmuteLabel : button.dataset.muteLabel;
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      }

      resumeProductVideo(activeItem, video) {
        if (!activeItem.classList.contains('is-active')) return;
        if (video.ended) video.currentTime = 0;
        this.updateProductVideoControls(activeItem, video);
        const blocked = () => {
          if (!activeItem.classList.contains('is-active')) return;
          video.controls = true;
          this.querySelector('[data-product-video-play]')?.removeAttribute('hidden');
        };
        try {
          video.play()?.then(() => {
            if (!activeItem.classList.contains('is-active')) video.pause();
            else this.querySelector('[data-product-video-play]')?.setAttribute('hidden', '');
          }).catch(blocked);
        } catch (error) {
          blocked();
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
